import { EQUIP_SLOTS, type EquipSlot, type ItemInstance } from "../items/types.js";
import { createGrid, type GridState } from "./grid.js";

/**
 * A BODY: the equipment a corpse is still wearing. A corpse keeps the shape of
 * the inventory it was — seven slots plus the pockets grid — with a worn rig or
 * backpack in its WRAPPED form (`{ ...bag, contents }`), so the looter reaches
 * into the dead man's rig with the same `"inside"` addressing as a box in a
 * crate.
 *
 * THE PSEUDO-GRID: `applyInventoryOp` thinks in grids, so the body's slots are
 * presented AS one — one row, `EQUIP_SLOTS.length` wide, slot `i` at cell
 * `(i, 0)` — joining the search set last. It is NOT a destination: no
 * `TargetRef` names it, so the dead do not get dressed. The two conversions are
 * exact inverses; the mapping is `EQUIP_SLOTS` order, append-only.
 */
export type EquipmentRecord = Record<EquipSlot, ItemInstance | null>;

/** An `EquipmentRecord` with nothing in it. */
export function emptyEquipment(): EquipmentRecord {
  const out = {} as EquipmentRecord;
  for (const slot of EQUIP_SLOTS) out[slot] = null;
  return out;
}

/** The slots as the one-row pseudo-grid the ops layer searches. */
export function bodySlotsGrid(equipment: EquipmentRecord): GridState {
  const grid = createGrid(EQUIP_SLOTS.length, 1);
  EQUIP_SLOTS.forEach((slot, i) => {
    const item = equipment[slot];
    if (item !== null) grid.items.push({ item, x: i, y: 0, rot: 0 });
  });
  return grid;
}

/** The record a pseudo-grid stands for. Cells no op could have produced are ignored. */
export function equipmentFromBodySlots(grid: GridState): EquipmentRecord {
  const out = emptyEquipment();
  for (const p of grid.items) {
    const slot = EQUIP_SLOTS[p.x];
    if (slot !== undefined && p.y === 0) out[slot] = p.item;
  }
  return out;
}

/** Slot-by-slot reference equality — the cheap "did an op touch the body" test. */
export function sameEquipment(a: EquipmentRecord, b: EquipmentRecord): boolean {
  for (const slot of EQUIP_SLOTS) {
    if (a[slot] !== b[slot]) return false;
  }
  return true;
}
